import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Stack, Switch, Autocomplete, TextField } from '@mui/material';
import React, { useState, useEffect, useCallback } from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { ClientDTO, getArrangementLevelPage, replaceFeeArrangementLevel } from 'wisestep-api';
import { WsButton, WsColors, WsDataTable, WsSelect } from 'wisestep-base';

import { SettingsPageLayout } from '../../../components/SettingsPageLayout';
import { useWorkspaceContext } from '../../../context';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import { AtsCustomAPIAccess, useAtsCustomAPIAccess } from '../../../hooks/useAtsCustomAPIAccess';
import { warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { clientSettingsIndexOptions, getClients } from '../helper/ClientSettingsHelper';

import AddEditArrangementLevelDialog from './AddEditArrangementLevelDialog';

export interface ArrangementLevel {
  createdBy?: string;
  createdOn?: Date;
  modifiedOn?: Date;
  modifiedBy?: string;
  client: ClientDTO;
  name: string;
  workspaceId?: any;
  active?: boolean;
  approved?: boolean;
  value?: string;
  id?: string;
}

const visibilityFilterSelectArray = [
  {
    label: 'Visible',
    value: 'Visible',
  },
  {
    label: 'Hidden',
    value: 'Hidden',
  },
];

export interface FeeArrangementLevelPage {
  totalItems?: number;
  totalPages?: number;
  currentPage?: number;
  levels: ArrangementLevel[];
}

const arrangementLevelFormDefaultValues = {
  client: {
    name: '',
    id: '',
  },
  name: '',
};

const FeeArrangementLevelSettings = () => {
  const methods = useForm();
  const [page, setPage] = useState(0);
  const [isOpenAddEditArrangementLevel, setOpenAddEditArrangementLevel] = useState<boolean>(false);
  const [addEditDialogData, setAddEditDialogData] = useState(arrangementLevelFormDefaultValues);
  const [arrangementLevelPage, setArrangementLevelPage] = useState<FeeArrangementLevelPage>({
    levels: [],
  });
  const [clientOptions, setClientOptions] = useState([]);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const workspaceContext = useWorkspaceContext();
  const { access: atsCustomAccessControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.MANAGE_CLIENT_SETTINGS,
  ]);
  const manageAccess = workspaceContext.owns || atsCustomAccessControl[0];

  const fetchPage = useCallback(async () => {
    const response = await getArrangementLevelPage(page, methods.getValues());
    response.data.levels = response.data.levels.map((level) => ({
      ...level,
      id: level.value,
      client: {
        ...level.client,
        id: level.client.value,
      },
    }));
    setArrangementLevelPage(response.data);
  }, [methods, page]);

  useEffect(() => {
    fetchPage();
  }, [page, fetchPage]);

  const renderEditCell = (props) => {
    const row: ArrangementLevel = props.row.original;
    return (
      <EditIcon
        sx={{ color: WsColors.TheBlues500 }}
        onClick={() => {
          if (manageAccess) {
            setAddEditDialogData(JSON.parse(JSON.stringify(row)));
            setOpenAddEditArrangementLevel(true);
          } else {
            setNotification(warning('You do not have enough permissions to perform this action'));
            return;
          }
        }}
      />
    );
  };

  const renderVisibilityCell = (props) => {
    const row: ArrangementLevel = props.row.original;
    return (
      <Switch
        disabled={!manageAccess}
        defaultChecked={row.active}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          replaceFeeArrangementLevel({ ...row, active: event.target.checked }).then((response) => {
            setArrangementLevelPage((prevPage) => ({
              ...prevPage,
              levels: prevPage.levels.map((l) => (l.id === row.id ? response.data : l)),
            }));
          });
        }}
      />
    );
  };

  const columns = [
    {
      Header: 'Client Name',
      accessor: (row) => row.client.name,
    },
    {
      Header: 'Level',
      accessor: 'name',
    },
    {
      Header: 'Visibility',
      accessor: 'active',
      Cell: renderVisibilityCell,
    },
    {
      Header: 'Created By',
      accessor: 'createdBy',
    },
    {
      Header: '',
      accessor: 'edit',
      Cell: renderEditCell,
    },
  ];

  const handleSearch = () => {
    page === 0 ? fetchPage() : setPage(0);
  };

  return (
    <>
      <SettingsPageLayout
        title="Fee Arrangement Levels"
        indexOptions={clientSettingsIndexOptions}
        breadCrumnbsList={getSettingBreadCrumbList(
          ISettingBreadCrumbTypes.CLIENTS,
          Pages['FEE_ARRANGEMENT_LEVELS']
        )}
      >
        <Stack spacing={2} margin={2}>
          {manageAccess && (
            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
              <WsButton
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={() => {
                  setAddEditDialogData(arrangementLevelFormDefaultValues);
                  setOpenAddEditArrangementLevel(true);
                }}
              >{`Add Arrangement Level`}</WsButton>
            </Stack>
          )}
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSearch)}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ flexGrow: 1 }}>
                  <Controller
                    control={methods.control}
                    name="clientFilter"
                    render={({ field: { onChange } }) => (
                      <Autocomplete
                        size="small"
                        multiple={true}
                        selectOnFocus
                        handleHomeEndKeys
                        options={clientOptions}
                        getOptionLabel={(option: any) => {
                          if (typeof option === 'string') {
                            return option;
                          }
                          if (option.inputValue) {
                            return option.inputValue;
                          }
                          return option.name;
                        }}
                        onChange={(event, item) => {
                          onChange(item);
                        }}
                        onInputChange={(event, value) => {
                          getClients(value).then(setClientOptions);
                        }}
                        renderInput={(params) => <TextField {...params} label="Search client..." />}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ width: '8em' }}>
                  <WsSelect
                    placeholder="Visibility"
                    name="visibility"
                    size="small"
                    selectArray={visibilityFilterSelectArray}
                  />
                </Box>
                <WsButton type="submit">Search</WsButton>
              </Stack>
            </form>
          </FormProvider>
          <WsDataTable
            pageable={true}
            page={page + 1}
            onPageChange={setPage}
            totalDataItems={arrangementLevelPage?.totalItems}
            fetchPage={setPage}
            columns={columns}
            data={arrangementLevelPage.levels as []}
          />
        </Stack>
      </SettingsPageLayout>
      {isOpenAddEditArrangementLevel && (
        <AddEditArrangementLevelDialog
          isOpen={isOpenAddEditArrangementLevel}
          onCancel={() => {
            setOpenAddEditArrangementLevel(false);
          }}
          onSave={() => {
            fetchPage();
            setOpenAddEditArrangementLevel(false);
          }}
          defaultValues={addEditDialogData}
        />
      )}
    </>
  );
};

export default FeeArrangementLevelSettings;
